import { z } from "zod";

/**
 * Parametres que `public/embed.js` place dans l'URL de l'iframe.
 *
 * C'est le contrat entre le snippet colle chez le studio et le widget. Il est
 * decrit ici plutot que devine a chaque endroit qui en a besoin : les hooks
 * lisent `window.location.search` chacun de leur cote, et tant que ce fichier
 * ne listait ni `pricingIds` ni `teacherId` il decrivait un contrat qui
 * n'existait plus -- une source d'erreur pour qui s'y fie avant de modifier
 * embed.js.
 *
 * Tout est optionnel sauf `studioId` : un snippet ancien, colle il y a des mois
 * sur le site d'un studio, ne porte pas les attributs ajoutes depuis. Rejeter
 * ces URLs viderait le widget d'un client qui n'a rien change.
 */
export const WidgetConfigSchema = z.object({
  studioId: z.string().min(1),
  /**
   * Absent : la langue par defaut du studio. embed.js ne l'envoie que si le
   * snippet porte data-locale (avant, il imposait "fr").
   */
  locale: z.string().optional(),
  /**
   * <html lang> de la page hote, envoye par embed.js quand data-locale est
   * absent. Suivi quand l'admin laisse la langue sur « Automatique ».
   */
  pageLocale: z.string().max(35).optional(),
  theme: z.enum(["light", "dark"]).default("light"),
  primary: z.string().optional(),
  background: z.string().optional(),
  layout: z
    .enum(["calendar", "agenda", "event", "packages", "private-booking"])
    .default("agenda"),

  /** Professeur mis en avant par le widget cours prives. */
  teacherId: z.string().optional(),

  /**
   * "id1,id2" : les tarifs que CE widget affiche, epingles par le snippet.
   * Absent, le widget retombe sur la selection enregistree pour le studio.
   */
  pricingIds: z.string().optional(),

  /** data-height : hauteur maximale de l'iframe ; au-dela le widget defile. */
  maxHeight: z.coerce.number().int().positive().optional(),

  /** data-font="inherit" : pile de polices lue sur la page hote. */
  font: z.string().max(200).optional(),

  /** Origine de la page hote, pour renvoyer les emails vers le site du studio. */
  returnUrl: z.string().optional(),

  /** Chemin de la page hote : avec returnUrl, la page notee sur la reservation. */
  hostPath: z.string().optional(),

  /** Retour d'un paiement (Stripe, ou #bz-booking= sur la page hote). */
  bz_booking: z.string().optional(),

  v: z.string().default("1.0"),
});

export type WidgetConfig = z.infer<typeof WidgetConfigSchema>;

/**
 * Lit les parametres de l'URL courante.
 *
 * `safeParse` plutot que `parse` : une URL malformee doit degrader le widget,
 * pas le faire planter en blanc sur le site d'un studio.
 */
export function parseWidgetConfig(
  search: string,
): { ok: true; config: WidgetConfig } | { ok: false; error: z.ZodError } {
  const params = Object.fromEntries(new URLSearchParams(search).entries());
  const result = WidgetConfigSchema.safeParse(params);

  return result.success
    ? { ok: true, config: result.data }
    : { ok: false, error: result.error };
}
